/**
 * Renders the sectioned results of Claude's final video synthesis:
 * scene breakdown, people, objects, events, emotions, detected text.
 */
import React from 'react';

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-card p-5">
      <h3 className="font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Tag({ children, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    green: 'bg-green-100 text-green-700',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${tones[tone]}`}>{children}</span>;
}

export default function SummaryPanel({ finalAnalysis = {} }) {
  const {
    scene_breakdown: sceneBreakdown = [],
    dialogue_summary: dialogueSummary = [],
    people = [],
    objects = [],
    events = [],
    emotions = [],
    text_detected: textDetected = [],
    uncertainties = [],
  } = finalAnalysis;

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Section title="Scene Breakdown">
        {sceneBreakdown.length ? (
          <ul className="space-y-2">
            {sceneBreakdown.map((s, i) => (
              <li key={i} className="text-sm">
                <span className="font-mono text-brand-600 mr-2">{s.timestamp}</span>
                {s.description}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400 text-sm">No scenes identified.</p>
        )}
      </Section>

      <Section title="Dialogue Summary">
        {dialogueSummary.length ? (
          <ul className="space-y-2">
            {dialogueSummary.map((d, i) => (
              <li key={i} className="text-sm">
                <span className="font-medium">{d.speaker}: </span>
                {d.summary}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400 text-sm">No dialogue detected.</p>
        )}
      </Section>

      <Section title="People Detected">
        {people.length ? (
          <ul className="space-y-2">
            {people.map((p, i) => (
              <li key={i} className="text-sm">
                <span className="font-medium">{p.id || `Person ${i + 1}`}</span> — {p.description}
                {p.first_seen && <span className="text-gray-400 ml-2 font-mono text-xs">first seen {p.first_seen}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400 text-sm">No people identified.</p>
        )}
      </Section>

      <Section title="Objects Detected">
        {objects.length ? (
          <div className="flex flex-wrap gap-1.5">
            {objects.map((o, i) => (
              <Tag key={i}>{o.name}{o.context ? ` — ${o.context}` : ''}</Tag>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-sm">No objects identified.</p>
        )}
      </Section>

      <Section title="Important Events">
        {events.length ? (
          <ul className="space-y-2">
            {events.map((e, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="font-mono text-brand-600">{e.timestamp}</span>
                <Tag tone={e.importance === 'high' ? 'red' : e.importance === 'medium' ? 'yellow' : 'gray'}>
                  {e.importance}
                </Tag>
                <span>{e.description}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400 text-sm">No key events identified.</p>
        )}
      </Section>

      <Section title="Emotions">
        {emotions.length ? (
          <ul className="space-y-2">
            {emotions.map((e, i) => (
              <li key={i} className="text-sm">
                <span className="font-mono text-brand-600 mr-2">{e.timestamp}</span>
                {e.description}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400 text-sm">No emotional cues identified.</p>
        )}
      </Section>

      <Section title="Detected Text (OCR)">
        {textDetected.length ? (
          <ul className="space-y-2">
            {textDetected.map((t, i) => (
              <li key={i} className="text-sm">
                <span className="font-mono text-brand-600 mr-2">{t.timestamp}</span>
                &ldquo;{t.text}&rdquo;
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400 text-sm">No on-screen text detected.</p>
        )}
      </Section>

      {uncertainties.length > 0 && (
        <Section title="Uncertainties">
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
            {uncertainties.map((u, i) => (
              <li key={i}>{u}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
